import React, { useEffect, useState } from 'react';
import { Achievement } from '../types/mundus';

interface AchievementUnlockModalProps {
  achievement: Achievement | null;
  isOpen: boolean;
  onClose: () => void;
}

export const AchievementUnlockModal: React.FC<AchievementUnlockModalProps> = ({
  achievement,
  isOpen,
  onClose,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen && achievement) {
      setShowConfetti(true);
      // Auto-close after 5 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, achievement]);

  const handleClose = () => {
    setShowConfetti(false);
    onClose();
  };

  if (!isOpen || !achievement) {
    return null;
  }

  const getTierStyles = (tier: string): {gradient: string; border: string; glow: string; text: string} => {
    switch (tier) {
      case 'bronze':
        return {
          gradient: 'from-amber-600 to-amber-800',
          border: 'border-amber-600',
          glow: 'shadow-amber-600/50',
          text: 'text-amber-500',
        };
      case 'silver':
        return {
          gradient: 'from-gray-400 to-gray-600',
          border: 'border-gray-400',
          glow: 'shadow-gray-400/50',
          text: 'text-gray-400',
        };
      case 'gold':
        return {
          gradient: 'from-yellow-400 to-yellow-600',
          border: 'border-yellow-400',
          glow: 'shadow-yellow-400/50',
          text: 'text-yellow-400',
        };
      case 'platinum':
        return {
          gradient: 'from-purple-400 to-purple-600',
          border: 'border-cyber-neon-purple',
          glow: 'shadow-purple-500/50',
          text: 'text-cyber-neon-purple',
        };
      default:
        return {
          gradient: 'from-gray-400 to-gray-600',
          border: 'border-gray-500',
          glow: 'shadow-gray-500/50',
          text: 'text-gray-400',
        };
    }
  };

  const tierStyles = getTierStyles(achievement.tier);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-cyber-bg-primary/90 backdrop-blur-sm z-40 transition-opacity animate-slide-in"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`cyber-card border-4 ${tierStyles.border} shadow-2xl ${tierStyles.glow} max-w-md w-full p-8 relative overflow-hidden bg-cyber-bg-secondary/95 backdrop-blur-md`}
          style={{
            animation: 'bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          }}
        >
          {/* Confetti effect */}
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(40)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-3 h-3 animate-confetti rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: '-10px',
                    background: `linear-gradient(135deg, ${
                      ['#00d9ff', '#ff00ff', '#39ff14', '#fbbf24', '#a855f7'][
                        Math.floor(Math.random() * 5)
                      ]
                    }, transparent)`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    animationDuration: `${2 + Math.random() * 2}s`,
                  }}
                />
              ))}
            </div>
          )}

          {/* Animated border glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className={`absolute inset-0 bg-gradient-to-r ${tierStyles.gradient} opacity-20 animate-pulse`}></div>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-cyber-text-muted hover:text-cyber-neon-pink transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Content */}
          <div className="text-center relative z-10">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-3xl font-display font-bold neon-text mb-3 uppercase tracking-wider animate-pulse">
                ACHIEVEMENT UNLOCKED!
              </h2>
              <div
                className={`inline-block px-4 py-2 rounded-full text-sm font-display font-bold text-white uppercase tracking-wider bg-gradient-to-r ${tierStyles.gradient} shadow-lg`}
              >
                {achievement.tier} TIER
              </div>
            </div>

            {/* Achievement Icon */}
            <div className="mb-6 transform scale-125">
              <div className="text-8xl mb-2 animate-float drop-shadow-2xl">
                {achievement.iconUrl}
              </div>
            </div>

            {/* Achievement Details */}
            <div className="mb-6 border-t border-b border-cyber-border py-6">
              <h3 className="text-2xl font-display font-bold text-cyber-text-primary mb-3 uppercase tracking-wider">
                {achievement.title}
              </h3>
              <p className="text-cyber-text-secondary font-mono text-sm leading-relaxed">
                {achievement.description}
              </p>
            </div>

            {/* Points */}
            <div className={`mb-6 p-5 cyber-card border-2 ${tierStyles.border} bg-cyber-bg-tertiary relative overflow-hidden`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${tierStyles.gradient} opacity-10`}></div>
              <p className="text-xs text-cyber-text-muted font-mono mb-2 uppercase tracking-wider relative z-10">
                POINTS EARNED
              </p>
              <p className={`text-5xl font-display font-bold ${tierStyles.text} relative z-10 drop-shadow-lg`}>
                +{achievement.points}
              </p>
            </div>

            {/* Action Button */}
            <button
              onClick={handleClose}
              className="cyber-button w-full text-lg shadow-2xl hover:scale-105 transform transition-all"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>🚀</span>
                <span>AWESOME!</span>
              </span>
            </button>

            {/* Auto-close indicator */}
            <p className="mt-4 text-xs text-cyber-text-muted font-mono">
              Auto-closes in 5s
            </p>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes bounceIn {
            0% {
              opacity: 0;
              transform: scale(0.3) rotate(-10deg);
            }
            50% {
              opacity: 1;
              transform: scale(1.05) rotate(2deg);
            }
            70% {
              transform: scale(0.95) rotate(-1deg);
            }
            100% {
              transform: scale(1) rotate(0deg);
            }
          }

          @keyframes confetti {
            0% {
              transform: translateY(0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(720deg);
              opacity: 0;
            }
          }

          .animate-confetti {
            animation: confetti linear forwards;
          }
        `}
      </style>
    </>
  );
};

export default AchievementUnlockModal;
